(() => {
  const SDK_URL = 'https://sdk.ugv.skeepers.io/loader.js';
  const SDK_TIMEOUT = 15000;

  const loadSkeepersSdk = () => {
    if (window.gwtSkeepersSdkPromise) return window.gwtSkeepersSdkPromise;

    window.gwtSkeepersSdkPromise = new Promise((resolve, reject) => {
      let settled = false;
      let timeout;

      const complete = (callback, value) => {
        if (settled) return;
        settled = true;
        window.clearTimeout(timeout);
        callback(value);
      };

      timeout = window.setTimeout(
        () => complete(reject, new Error('Skeepers SDK timed out.')),
        SDK_TIMEOUT
      );

      window.onTeesterReady = window.onTeesterReady || [];
      window.onTeesterReady.push((SDK) => complete(resolve, SDK));

      let loader = document.querySelector(`script[src="${SDK_URL}"]`);
      if (!loader) {
        loader = document.createElement('script');
        loader.src = SDK_URL;
        loader.async = true;
        loader.defer = true;
        loader.dataset.skeepersUgcLoader = 'true';
        document.head.appendChild(loader);
      }

      loader.addEventListener(
        'error',
        () => {
          if (loader.dataset.skeepersUgcLoader === 'true') loader.remove();
          complete(reject, new Error('Skeepers SDK failed to load.'));
        },
        { once: true }
      );
    });

    window.gwtSkeepersSdkPromise = window.gwtSkeepersSdkPromise.catch((error) => {
      window.gwtSkeepersSdkPromise = null;
      throw error;
    });

    return window.gwtSkeepersSdkPromise;
  };

  class SkeepersUgcCarousel extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.playerKey = this.dataset.playerKey;
      this.productReference = this.dataset.productReference;
      this.productId = this.dataset.productId;
      this.template = this.querySelector('[data-carousel-template]');
      this.renderTarget = this.querySelector('[data-render-target]');
      this.variantChangeHandler = this.handleVariantChange.bind(this);
      document.addEventListener('on:variant:change', this.variantChangeHandler);

      if (!this.playerKey || !this.productReference || !this.template || !this.renderTarget) {
        this.hide();
        return;
      }

      if ('IntersectionObserver' in window) {
        this.sectionObserver = new IntersectionObserver(
          (entries) => {
            if (!entries.some((entry) => entry.isIntersecting)) return;
            this.sectionObserver.disconnect();
            this.loadVideos(this.productReference);
          },
          { rootMargin: '400px 0px' }
        );
        this.sectionObserver.observe(this);
      } else {
        this.loadVideos(this.productReference);
      }
    }

    disconnectedCallback() {
      document.removeEventListener('on:variant:change', this.variantChangeHandler);
      this.sectionObserver?.disconnect();
    }

    handleVariantChange(event) {
      const eventProductId = event.detail?.product?.id;
      if (eventProductId && String(eventProductId) !== this.productId) return;

      const nextReference = event.detail?.variant?.sku?.trim() || '';
      if (nextReference === this.productReference) return;

      this.productReference = nextReference;
      if (!nextReference) {
        this.requestId = (this.requestId || 0) + 1;
        this.hide();
        return;
      }

      if (!this.hasLoaded) {
        this.hidden = false;
        return;
      }

      this.sectionObserver?.disconnect();
      this.loadVideos(nextReference);
    }

    async loadVideos(productReference) {
      const requestId = (this.requestId || 0) + 1;
      this.requestId = requestId;
      this.hasLoaded = true;
      this.hidden = false;
      this.setAttribute('aria-busy', 'true');
      this.renderTarget.replaceChildren();

      try {
        const SDK = await loadSkeepersSdk();
        const videos = await this.getVideos(SDK, productReference);
        if (requestId !== this.requestId) return;

        const uniqueVideos = videos.filter((video, index, allVideos) => (
          video?.id && allVideos.findIndex((item) => item?.id === video.id) === index
        ));

        if (!uniqueVideos.length) {
          this.hide();
          return;
        }

        this.render(uniqueVideos);
      } catch (error) {
        if (requestId === this.requestId) this.hide();
      }
    }

    getVideos(SDK, productReference) {
      return new Promise((resolve, reject) => {
        let settled = false;
        let timeout;

        const complete = (callback, value) => {
          if (settled) return;
          settled = true;
          window.clearTimeout(timeout);
          callback(value);
        };

        timeout = window.setTimeout(
          () => complete(reject, new Error('Skeepers videos timed out.')),
          SDK_TIMEOUT
        );

        try {
          const result = SDK.getVideos(this.playerKey, productReference, (videos) => {
            complete(resolve, Array.isArray(videos) ? videos : []);
          });

          if (result && typeof result.then === 'function') {
            result.then(
              (videos) => complete(resolve, Array.isArray(videos) ? videos : []),
              (error) => complete(reject, error)
            );
          }
        } catch (error) {
          complete(reject, error);
        }
      });
    }

    render(videos) {
      const fragment = this.template.content.cloneNode(true);
      const carousel = fragment.querySelector('carousel-slider');
      const slides = fragment.querySelector('[data-skeepers-slides]');
      const controls = fragment.querySelector('[data-skeepers-controls]');

      videos.forEach((video, index) => {
        const slide = document.createElement('li');
        const frame = document.createElement('div');
        const player = document.createElement('skeepers-player');
        const title = video.titles?.original || video.titles?.short || `${index + 1} / ${videos.length}`;

        slide.className = 'slider__item skeepers-ugc__slide';
        slide.setAttribute('role', 'group');
        slide.setAttribute('aria-roledescription', 'slide');
        slide.setAttribute('aria-label', `${index + 1} / ${videos.length}: ${title}`);

        frame.className = 'skeepers-ugc__player-frame';

        player.className = 'skeepers-ugc__player';
        player.setAttribute('key', this.playerKey);
        player.setAttribute('id', video.id);
        player.setAttribute('width', '100%');
        player.setAttribute('height', '100%');
        player.setAttribute('allow_buy', '1');
        player.setAttribute('label', `ugc-${this.dataset.sectionId}-${video.id}`);

        frame.appendChild(player);
        slide.appendChild(frame);
        slides.appendChild(slide);
      });

      if (videos.length < 2) {
        controls.hidden = true;
        carousel.removeAttribute('aria-roledescription');
      }

      this.renderTarget.replaceChildren(fragment);
      this.hidden = false;
      this.setAttribute('aria-busy', 'false');
    }

    hide() {
      this.hidden = true;
      this.setAttribute('aria-busy', 'false');
      this.renderTarget?.replaceChildren();
    }
  }

  if (!customElements.get('skeepers-ugc-carousel')) {
    customElements.define('skeepers-ugc-carousel', SkeepersUgcCarousel);
  }
})();
